import { useState, useRef, type ChangeEvent, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';

/**
 * Phase 13 — Import + Export tools panel.
 *
 * Lives at the bottom of the stories sidebar. Two affordances:
 *  - Export dropdown: json / csv / markdown → triggers a streamed
 *    download via the `Bearer`-token-protected /v1/.../stories/export.
 *  - Import file picker: posts a JSON file to /v1/.../stories/import,
 *    shows a count of imported / skipped / errored rows.
 *
 * Bulk-action multi-select on the tree is a follow-up increment —
 * the API is in place for SDK / curl callers in the meantime.
 */
export function BulkToolsPanel(): JSX.Element | null {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [statusKind, setStatusKind] = useState<'ok' | 'err' | null>(null);

  if (token === null || spaceSlug === null) {
    return null;
  }

  function pickFormat(format: 'json' | 'csv' | 'markdown'): void {
    if (token === null || spaceSlug === null) return;
    const api = makeApi(token);
    // Build the URL then fetch with the auth Bearer; the streamed body
    // becomes a blob we hand to the browser's download mechanism. We
    // can't just window.location.href because the request needs the
    // Authorization header (server-side cookies aren't shared with
    // the SDK's Bearer flow).
    const url = api.exportStoriesUrl(spaceSlug, format);
    setExportOpen(false);
    setStatusKind(null);
    setStatusText('Exportiere…');
    void fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Export fehlgeschlagen: HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `peacock-export-${spaceSlug}-${new Date()
          .toISOString()
          .slice(0, 10)}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        setStatusKind('ok');
        setStatusText(`Heruntergeladen: ${format}.`);
      })
      .catch((err: Error) => {
        setStatusKind('err');
        setStatusText(err.message);
      });
  }

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      if (token === null || spaceSlug === null) {
        throw new Error('import ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.importStories(spaceSlug, file);
      if (result.status !== 'ok') {
        // The discriminated-union covers unauthenticated / forbidden /
        // not-found / error variants; only the 'error' variant carries
        // an HTTP code. Show whatever signal is available.
        const code = 'code' in result ? result.code : result.status;
        throw new Error(`Import fehlgeschlagen: ${code}`);
      }
      return result.data.data;
    },
    onSuccess: (data) => {
      setStatusKind('ok');
      setStatusText(
        `Importiert: ${data.imported} · Übersprungen: ${data.skipped}` +
          (data.errors.length > 0
            ? ` · ${data.errors.length} Fehler (siehe Konsole)`
            : '')
      );
      if (data.errors.length > 0) {
        // Surface details via the dev-console — full table would be
        // a separate "import-report" dialog (follow-up).
        console.warn('[peacock import] errors:', data.errors);
      }
    },
    onError: (err: Error) => {
      setStatusKind('err');
      setStatusText(err.message);
    },
  });

  function onFileChosen(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    importMutation.mutate(file);
    // Reset so the same file can be re-picked.
    e.target.value = '';
  }

  return (
    <div
      className="mt-3 border-t border-zinc-200 pt-3 text-xs"
      data-testid="bulk-tools-panel"
    >
      <h3 className="mb-2 px-2 font-semibold uppercase tracking-wider text-zinc-500">
        Tools
      </h3>
      <div className="flex flex-col gap-1.5 px-2">
        <div className="relative">
          <button
            type="button"
            onClick={() => setExportOpen((s) => !s)}
            data-testid="export-button"
            className="inline-flex w-full items-center gap-1.5 rounded border border-zinc-200 bg-white px-2 py-1.5 text-left hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
          >
            <span aria-hidden="true">📤</span>
            <span>Export</span>
            <span aria-hidden="true">▾</span>
          </button>
          {exportOpen && (
            <div
              role="menu"
              className="absolute left-0 right-0 top-full z-10 mt-1 flex flex-col rounded-md border border-zinc-200 bg-white shadow-lg"
            >
              {(['json', 'csv', 'markdown'] as const).map((fmt) => (
                <button
                  key={fmt}
                  type="button"
                  role="menuitem"
                  onClick={() => pickFormat(fmt)}
                  data-testid={`export-format-${fmt}`}
                  className="px-3 py-2 text-left hover:bg-zinc-50"
                >
                  {fmt === 'json'
                    ? 'JSON · vollständig (Re-Import-fähig)'
                    : fmt === 'csv'
                      ? 'CSV · tabellarisch'
                      : 'Markdown · les-/druckbar'}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={importMutation.isPending}
          data-testid="import-button"
          className="inline-flex w-full items-center gap-1.5 rounded border border-zinc-200 bg-white px-2 py-1.5 text-left hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:opacity-50"
        >
          <span aria-hidden="true">📥</span>
          <span>{importMutation.isPending ? 'Importiere…' : 'Import (.json)'}</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={onFileChosen}
          className="hidden"
          data-testid="import-file-input"
        />
      </div>
      {statusText !== null && (
        <p
          role="status"
          className={
            'mx-2 mt-2 rounded border px-2 py-1.5 ' +
            (statusKind === 'err'
              ? 'border-red-200 bg-red-50 text-red-900'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900')
          }
        >
          {statusText}
        </p>
      )}
    </div>
  );
}
